import React from "react";
import { AlertTriangle, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./Button";

interface ConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: "danger" | "warning" | "info";
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    variant = "danger",
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fadeIn"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-scaleIn border border-gray-100">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className={cn(
                            "p-3 rounded-xl",
                            variant === "danger" ? "bg-red-50 text-red-600" :
                                variant === "warning" ? "bg-amber-50 text-amber-600" :
                                    "bg-blue-50 text-blue-600"
                        )}>
                            <AlertTriangle className="h-6 w-6" />
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
                    <p className="text-gray-600 leading-relaxed">{message}</p>
                </div>

                <div className="bg-gray-50 px-6 py-4 flex flex-col-reverse sm:flex-row justify-end gap-3 mt-2">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        className="sm:w-auto w-full border-gray-200 text-gray-700 hover:bg-gray-100"
                    >
                        {cancelLabel}
                    </Button>
                    <Button
                        onClick={() => {
                            onConfirm();
                            onClose();
                        }}
                        className={cn(
                            "sm:w-auto w-full text-white shadow-sm",
                            variant === "danger" ? "bg-red-600 hover:bg-red-700" :
                                variant === "warning" ? "bg-[#885ABB] hover:bg-[#7748A4]" :
                                    "bg-blue-600 hover:bg-blue-700"
                        )}
                    >
                        {confirmLabel}
                    </Button>
                </div>
            </div>
        </div>
    );
};
